import type { IconName } from '@fortawesome/fontawesome-common-types';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';
import { array, boolean, object, string } from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import DrilldownItem from '@components/DrilldownItem';
import { FormikFhirDateField, FormikTextField } from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';
import RelatedRecordsForm from '@components/RelatedRecordsTable/RelatedRecordsForm';

import { SURGERIES } from '@constants/routes';

import { useCreateProcedure, useUpdateProcedure } from '@hooks/api/procedure';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useFormat } from '@hooks/useFormat';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';
import { ColumnLayout } from '@layouts/DrilldownStyle';

import { getProcedureTemplate } from '@models/fhir-templates/procedure';
import type {
  FormValues,
  ProcedureRecord,
  SurgeryValues,
} from '@models/procedure';
import type { DrilldownProps } from '@models/record-drilldown';

import { formatFacility } from '@utils/format';
import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';

import SharingDrilldownWrapper from '@views/Records/Common/SharingDrilldownWrapper';

import type { LocationModelInput } from 'generated/types';

import DrilldownWrapper from '../Common/DrilldownWrapper';

const messages = defineMessages({
  anesthesiologistName: {
    id: 'surgeriesDrilldown.anesthesiologistName',
    defaultMessage: 'Anesthesiologist Name',
  },
  preOpDiagnosis: {
    id: 'surgeriesDrilldown.preOpDiagnosis',
    defaultMessage: 'Preoperative Diagnosis',
  },
  postOpDiagnosis: {
    id: 'surgeriesDrilldown.postOpDiagnosis',
    defaultMessage: 'Post-operative Diagnosis',
  },
  procedureNotes: {
    id: 'surgeriesDrilldown.procedureNotes',
    defaultMessage: 'Procedure Notes',
  },
  complications: {
    id: 'surgeriesDrilldown.complications',
    defaultMessage: 'Complications',
  },
  elb: {
    id: 'surgeriesDrilldown.elb',
    defaultMessage: 'Estimated Blood Loss',
  },
  visitReason: {
    id: 'surgeriesDrilldown.visitReason',
    defaultMessage: 'Visit Reason',
  },
  plan: {
    id: 'surgeriesDrilldown.plan',
    defaultMessage: 'Plan',
  },
  performedOn: {
    id: 'surgeriesDrilldown.performedOn',
    defaultMessage: 'Performed on',
  },
  followUp: {
    id: 'surgeriesDrilldown.followUp',
    defaultMessage: 'Follow Up',
  },
  anesthesia: {
    id: 'surgeriesDrilldown.anesthesia',
    defaultMessage: 'Anesthesia',
  },
  practitioner: {
    id: 'GLOBAL.practitioner',
    defaultMessage: 'Practitioner',
  },
  facility: {
    id: 'GLOBAL.facility',
    defaultMessage: 'Facility',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  procedure: {
    id: 'surgeriesDrilldown.procedure',
    defaultMessage: 'Procedure',
  },
});

interface SurgeryDrilldownProps extends DrilldownProps<ProcedureRecord> {
  /**
   * When true, suppresses the default redirect to the SURGERIES route
   * after successfully creating a new procedure.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
}

const SurgeryDrilldown: React.FC<SurgeryDrilldownProps> = ({
  data,
  isCreating,
  isEditing,
  isSharing,
  loading: baseLoading,
  onSubmit,
  patientId,
  isCurator,
  disableNavigateOnCreate = false,
}) => {
  const { formatMessage } = useIntl();
  const { formatDateLabel } = useFormat();
  const { enqueueSnackbar } = useSnackbar();
  const isModifying = isCreating || isEditing;

  const [createProcedure, createProcedureLoading] = useCreateProcedure();
  const [updateProcedure, updateProcedureLoading] = useUpdateProcedure();
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();

  const loading =
    baseLoading ||
    createProcedureLoading ||
    updateProcedureLoading ||
    recordStatusAndSourceLoading;

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    title: data?.procedureName ?? '',
    anesthesia: data?.anesthesia ?? '',
    anesthesiologist: data?.anesthesiologist ?? '',
    city: data?.city ?? '',
    complications: data?.complications ?? '',
    country: data?.country ?? '',
    doctor: data?.doctor ?? '',
    elb: data?.elb ?? '',
    facility: data?.facility ?? '',
    followUp: data?.followUp ?? '',
    plan: data?.plan ?? '',
    postOperativeDiagnosis: data?.postOperativeDiagnosis ?? '',
    practitioner: data?.practitioner ?? '',
    preoperativeDiagnosis: data?.preoperativeDiagnosis ?? '',
    procedureDate: data?.procedureDate ?? '',
    procedureNotes: data?.procedureNotes ?? '',
    visitReason: data?.visitReason ?? '',
    selfReported: data?.selfReported ?? false,
    associatedResources: [],
  };

  const surgeryValidationSchema: Schema<Partial<SurgeryValues>> =
    object().shape({
      title: string().required(formatMessage(messages.required)),
      anesthesia: string(),
      anesthesiologist: string(),
      city: string(),
      complications: string(),
      country: string(),
      doctor: string(),
      elb: string(),
      facility: string(),
      followUp: string(),
      plan: string(),
      postOperativeDiagnosis: string(),
      practitioner: string(),
      preoperativeDiagnosis: string(),
      procedureDate: string(),
      procedureNotes: string(),
      selfReported: boolean().required(formatMessage(messages.required)),
      visitReason: string(),
      associatedResources: array().of(
        string().required(formatMessage(messages.required))
      ),
    });

  const validationSchema: Schema<
    Partial<FormValues>,
    any,
    any,
    Flags
  > = surgeryValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    if (!patientId) return;

    await validateForm(stripEmptyStrings({ ...values }));

    let surgery: ProcedureRecord | null | undefined;

    try {
      const procedure = data?.getModelInput(values);
      if (data?.id && procedure) {
        const location: LocationModelInput | null =
          values.city || values.country || values.facility
            ? {
                name: values.facility,
                ...((values.city || values.country) && {
                  address: {
                    city: values.city,
                    country: values.country,
                  },
                }),
              }
            : null;
        const { data: updatedProcedure } = await updateProcedure({
          input: {
            id: data.id,
            procedure,
            location,
          },
        });
        surgery = updatedProcedure;
      } else {
        const { procedure, location } = getProcedureTemplate(patientId, values);
        const { data } = await createProcedure({
          input: {
            procedure,
            location,
          },
        });
        surgery = data;
      }
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: surgery?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (surgery && isCreating && !disableNavigateOnCreate) {
        navigate(`${SURGERIES}${surgery.id}`);
      }
      onSubmit?.();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => {
        const children = (
          <>
            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.visitReason)}
                name="visitReason"
                value={data?.visitReason}
              />
              <DrilldownItem
                label={formatMessage(messages.anesthesiologistName)}
                name="anesthesiologist"
                value={data?.anesthesiologist}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.preOpDiagnosis)}
                name="preoperativeDiagnosis"
                value={data?.preoperativeDiagnosis}
              />
              <DrilldownItem
                label={formatMessage(messages.postOpDiagnosis)}
                name="postOperativeDiagnosis"
                value={data?.postOperativeDiagnosis}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                charLimit={100000}
                label={formatMessage(messages.procedureNotes)}
                name="procedureNotes"
                type="text-area"
                value={data?.procedureNotes}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.complications)}
                name="complications"
                value={data?.complications}
              />
              <DrilldownItem
                label={formatMessage(messages.elb)}
                name="elb"
                value={data?.elb}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.plan)}
                name="plan"
                value={data?.plan}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.anesthesia)}
                name="anesthesia"
                value={data?.anesthesia}
              />
              <DrilldownItem
                label={formatMessage(messages.followUp)}
                name="followUp"
                value={data?.followUp}
              />
            </ColumnLayout>

            <RelatedRecordsForm associations={values.associatedResources} />

            <RecordAttachmentForm
              isCreating={isCreating}
              onFileChange={(files: DroppedFile[]) => {
                setFieldValue('files', files);
              }}
              patientId={patientId}
              record={{
                attachments: data?.attachments,
                resourceId: data?.getResourceId(),
              }}
            />

            {isModifying && (
              <>
                <HorizontalRule />
                <RecordSubmitButton />
              </>
            )}
          </>
        );
        const drilldownProps = {
          items: [
            {
              editingComponent: (
                <FormikTextField
                  isHeaderItem
                  name="practitioner"
                  placeholder={formatMessage(messages.practitioner)}
                />
              ),
              title: data?.practitioner ?? 'Unknown',
              iconName: 'user-doctor' as IconName,
            },
            {
              editingComponent: (
                <>
                  <FormikTextField
                    isHeaderItem
                    name="facility"
                    placeholder={formatMessage(messages.facility)}
                  />
                  <FormikTextField
                    isHeaderItem
                    name="city"
                    placeholder={formatMessage(messages.city)}
                  />
                  <FormikTextField
                    isHeaderItem
                    name="country"
                    placeholder={formatMessage(messages.country)}
                  />
                </>
              ),
              title: formatFacility(
                data?.facility,
                data?.city,
                null,
                data?.country
              ),
              iconName: 'location-dot' as IconName,
            },
          ],
          loading,
          resource: data,
          selfReported: data?.selfReported,
          subtitle: isModifying ? (
            <FormikFhirDateField
              formats={['date', 'datetime', 'year']}
              label={formatMessage(messages.performedOn)}
              name="procedureDate"
              onChange={(value) => {
                setFieldValue('procedureDate', value);
              }}
              value={data?.procedureDate}
              max={true}
            />
          ) : (
            formatDateLabel(
              messages.performedOn,
              data?.getProcedureDateStr(true)
            )
          ),
          title: data?.procedureName || formatMessage(messages.procedure),
          titleInput: (
            <FormikTextField
              name="title"
              placeholder={formatMessage(messages.procedure)}
            />
          ),
        };

        return (
          <Form>
            {isSharing ? (
              <SharingDrilldownWrapper {...drilldownProps}>
                {children}
              </SharingDrilldownWrapper>
            ) : (
              <DrilldownWrapper
                associatedRecords={data?.associatedRecords}
                backRoute={SURGERIES}
                notes={data?.recordNotes}
                {...drilldownProps}
              >
                {children}
              </DrilldownWrapper>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default SurgeryDrilldown;
